import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import LoginPage from "@/pages/LoginPage";
import EmergencyAccessPage from "@/pages/EmergencyAccessPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import DashboardPage from "@/pages/DashboardPage";
import InvoicesPage from "@/pages/InvoicesPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import {
  AdminOverview,
  AdminUsers,
  AdminInvoices,
  AdminCensus,
} from "@/pages/AdminPages";
import AuditPage from "@/pages/AuditPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminCompanies from "@/pages/AdminCompanies";
import AdminVendors from "@/pages/AdminVendors";
import AdminSecurity from "@/pages/AdminSecurity";
import AdminEmails from "@/pages/AdminEmails";
import HelpPage from "@/pages/HelpPage";
import AdminCompare from "@/pages/AdminCompare";
import AppShell from "@/components/AppShell";
import AttestationGate from "@/components/AttestationGate";
import AdminCarriers from "@/pages/AdminCarriers";
import AdminAttestations from "@/pages/AdminAttestations";
import AdminMigration from "@/pages/AdminMigration";
import DevBadge from "@/components/DevBadge";
import AdminRelease from "@/pages/AdminRelease";
import AdminLaunch from "@/pages/AdminLaunch";
import AdminBackup from "@/pages/AdminBackup";
import AdminBacklog from "@/pages/AdminBacklog";
import RoadmapPage from "@/pages/RoadmapPage";
import SessionInactivityGuard from "@/components/SessionInactivityGuard";
import { MaintenanceProvider } from "@/components/MaintenanceProvider";
import MaintenancePage from "@/pages/MaintenancePage";
import AdminMaintenance from "@/pages/AdminMaintenance";
import AdminSSO from "@/pages/AdminSSO";
import FilesPage from "@/pages/FilesPage";
import { ReleaseProvider, useRelease } from "@/lib/release";

function Protected({ children, adminOnly, employerOnly, invoiceOnly, flag }) {
  const { user, loading } = useAuth();
  const release = useRelease();
  if (loading || (user && release.loading))
    return (
      <div className="min-h-screen flex items-center justify-center eyebrow">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (flag && !release.isOn(flag))
    return (
      <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />
    );
  if (adminOnly && user.role !== "admin")
    return <Navigate to="/dashboard" replace />;
  if (employerOnly && user.role !== "customer")
    return <Navigate to="/admin" replace />;
  if (
    invoiceOnly &&
    user.role === "customer" &&
    user.invoice_service === false
  ) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <AttestationGate>
      <AppShell>{children}</AppShell>
    </AttestationGate>
  );
}

function IndexRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <MaintenanceProvider>
          <SessionInactivityGuard />
          <ReleaseProvider>
          <BrowserRouter>
            <DevBadge />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  borderRadius: 0,
                  background: "#0A1C16",
                  color: "#F7F7F6",
                  border: "1px solid #16382C",
                },
              }}
            />
            <Routes>
              <Route path="/" element={<IndexRedirect />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/emergency-access" element={<EmergencyAccessPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/maintenance" element={<MaintenancePage />} />

              <Route
                path="/dashboard"
                element={
                  <Protected>
                    <DashboardPage />
                  </Protected>
                }
              />
              <Route
                path="/invoices"
                element={
                  <Protected invoiceOnly>
                    <InvoicesPage />
                  </Protected>
                }
              />
              <Route
                path="/approvals"
                element={
                  <Protected flag="page.approvals" invoiceOnly>
                    <ApprovalsPage />
                  </Protected>
                }
              />
              <Route
                path="/analytics"
                element={
                  <Protected flag="page.prevue">
                    <AnalyticsPage />
                  </Protected>
                }
              />
              <Route
                path="/settings"
                element={
                  <Protected flag="page.settings">
                    <SettingsPage />
                  </Protected>
                }
              />
              <Route
                path="/roadmap"
                element={
                  <Protected flag="page.roadmap" employerOnly>
                    <RoadmapPage />
                  </Protected>
                }
              />
              <Route
                path="/files"
                element={
                  <Protected>
                    <FilesPage />
                  </Protected>
                }
              />

              <Route
                path="/admin"
                element={
                  <Protected adminOnly>
                    <AdminOverview />
                  </Protected>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <Protected adminOnly>
                    <AdminUsers />
                  </Protected>
                }
              />
              <Route
                path="/admin/invoices"
                element={
                  <Protected flag="page.admin_invoices" adminOnly>
                    <AdminInvoices />
                  </Protected>
                }
              />
              <Route
                path="/admin/census"
                element={<Navigate to="/admin/setup" replace />}
              />
              <Route
                path="/admin/setup"
                element={
                  <Protected flag="page.admin_setup" adminOnly>
                    <AdminCensus />
                  </Protected>
                }
              />
              <Route
                path="/admin/vendors"
                element={
                  <Protected flag="page.admin_vendors" adminOnly>
                    <AdminVendors />
                  </Protected>
                }
              />
              <Route
                path="/admin/security"
                element={
                  <Protected flag="page.admin_security" adminOnly>
                    <AdminSecurity />
                  </Protected>
                }
              />
              <Route
                path="/admin/release"
                element={
                  <Protected adminOnly>
                    <AdminRelease />
                  </Protected>
                }
              />
              <Route
                path="/admin/launch"
                element={
                  <Protected adminOnly>
                    <AdminLaunch />
                  </Protected>
                }
              />
              <Route
                path="/admin/sso"
                element={
                  <Protected adminOnly>
                    <AdminSSO />
                  </Protected>
                }
              />
              <Route
                path="/admin/maintenance"
                element={
                  <Protected adminOnly>
                    <AdminMaintenance />
                  </Protected>
                }
              />
              <Route
                path="/admin/backup"
                element={
                  <Protected flag="page.admin_backup" adminOnly>
                    <AdminBackup />
                  </Protected>
                }
              />
              <Route
                path="/admin/migration"
                element={
                  <Protected flag="page.admin_migration" adminOnly>
                    <AdminMigration />
                  </Protected>
                }
              />
              <Route
                path="/admin/backlog"
                element={
                  <Protected flag="page.admin_backlog" adminOnly>
                    <AdminBacklog />
                  </Protected>
                }
              />
              <Route
                path="/help"
                element={
                  <Protected flag="page.help">
                    <HelpPage />
                  </Protected>
                }
              />
              <Route
                path="/admin/emails"
                element={
                  <Protected flag="page.admin_emails" adminOnly>
                    <AdminEmails />
                  </Protected>
                }
              />
              <Route
                path="/admin/carriers"
                element={
                  <Protected flag="page.admin_carriers" adminOnly>
                    <AdminCarriers />
                  </Protected>
                }
              />
              <Route
                path="/admin/attestations"
                element={
                  <Protected flag="page.admin_attestations" adminOnly>
                    <AdminAttestations />
                  </Protected>
                }
              />
              <Route
                path="/admin/audit"
                element={
                  <Protected flag="page.admin_audit" adminOnly>
                    <AuditPage />
                  </Protected>
                }
              />
              <Route
                path="/admin/companies"
                element={
                  <Protected flag="page.admin_companies" adminOnly>
                    <AdminCompanies />
                  </Protected>
                }
              />
              <Route
                path="/admin/financials"
                element={<Navigate to="/admin/setup" replace />}
              />
              <Route
                path="/admin/compare"
                element={
                  <Protected flag="page.admin_compare" adminOnly>
                    <AdminCompare />
                  </Protected>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </ReleaseProvider>
        </MaintenanceProvider>
      </AuthProvider>
    </div>
  );
}
